<template>
  <qtm-dialog-card
    v-if="step == 1"
    v-model="localValue"
    closable
    header-background="white"
    :title="title"
    :subtitle="subtitle"
    width="520"
  >
    <div class="pt-4">
      <qtm-input-label v-if="selectCanceller" label="User who cancels the order" required>
        <qtm-select
          v-model="cancelledBy"
          :item-title="teamMemberName"
          :items="projectTeam"
          :loading="loading"
          persistent-hint
        />
      </qtm-input-label>

      <qtm-input-label label="Cancellation Reason" required>
        <v-radio-group v-model="cancellationReason" density="comfortable" hide-details>
          <div v-for="reason in cancellationReasons" :key="reason">
            <qtm-radio
              :label="reason"
              :true-value="reason"
              data-test="cancel-reason"
            />
            <div
              v-if="order.isRFQ && cancellationReason === reason && reason.toLowerCase() === 'unsatisfactory pricing'"
              class="py-3 qtm-body text-dark-grey"
            >
              You may want to
              <nuxt-link :to="{ name: 'orders-id-invite-suppliers', params: { id: order.id } }">
                invite more suppliers
              </nuxt-link>
              to this request, if it's still relevant.
            </div>
          </div>
        </v-radio-group>
      </qtm-input-label>

      <qtm-text-field
        v-if="cancellationReason === 'Other'"
        v-model="otherCancellationReason"
        class="mt-1"
        data-test="cancellation-reason-other"
        density="compact"
        hide-details
        placeholder="Please enter your other reason"
        :maxlength="CANCELLATION_REASON_MAX_LENGTH"
      />

      <qtm-input-label label="Additional Information" class="mt-4">
        <qtm-textarea
          v-model="cancellationMessage"
          auto-grow
          rows="2"
          hide-details
          placeholder="Details about this cancellation"
          data-test="cancellation-message"
        />
      </qtm-input-label>

      <qtm-input-label v-if="isPO" class="mt-4" label="Add Contacts (as CC)">
        <email-address-combobox
          v-model="cc"
          :addresses="userEmails"
          hide-details
          placeholder="Select from internal or add new contacts"
        />
      </qtm-input-label>

      <qtm-checkbox
        v-if="showDistribute"
        v-model="distribute"
        class="mt-8"
        label="Send the cancellation notice to the supplier"
      />
    </div>

    <template v-slot:actions>
      <qtm-btn
        size="large"
        style="width: 80%;"
        class="my-2 mx-12"
        data-test="confirmation-ok-button"
        :disabled="disabled"
        @click="step = 2"
      >
        {{ buttonLabel }}
      </qtm-btn>
    </template>
  </qtm-dialog-card>
  <qtm-dialog-card v-else-if="step == 2" v-model="localValue" width="520" @update:model-value="$emit('close')">
    <div class="qtm-h1 text-primary title-warning">
      WARNING
    </div>
    <div v-if="internalDistributionOnly" class="qtm-h1">
      Cancellation will not be sent to the supplier
    </div>
    <div v-else-if="isOrder" class="qtm-h1">
      CANCEL ORDER {{ order.reference_name }} - {{ order.qtm_reference_number }}
    </div>
    <div v-else class="qtm-h1">
      CANCEL PO {{ order.reference_name }} - {{ order.pos[0].po_number || order.qtm_reference_number }}
    </div>

    <p v-if="internalDistributionOnly" class="mt-2">
      There is no supplier contact set so they will not be informed of the cancellation. Do you want to continue?
    </p>
    <p v-else-if="isOrder" class="mt-2">
      Are you sure you want to cancel the order?
    </p>
    <p v-else class="mt-2">
      You are about to cancel {{ order.pos[0].po_number ? 'a committed' : 'this' }} purchase order.
      This cannot be undone. Do you want to proceed?
    </p>

    <template v-slot:actions>
      <v-spacer />
      <qtm-btn data-test="confirmation-cancel-button" :loading="loading" tertiary @click="step = 1">
        Not yet
      </qtm-btn>
      <qtm-btn class="my-2" data-test="confirmation-ok-button" :loading="loading" @click="cancelOrder">
        Yes, cancel {{ isOrder? 'Order ':'PO' }}
      </qtm-btn>
    </template>
  </qtm-dialog-card>
</template>

<script setup lang="ts">
import { OrderState } from '@quotetome/materials-api'
import { CANCELLATION_REASON_MAX_LENGTH } from '@/constants'
import EmailAddressCombobox from '@/components/emails/email-address-combobox.vue'
import type { Order, User } from '@quotetome/materials-api'

export interface Props {
  order: Order,
  modelValue: boolean,
  selectCanceller?: boolean,
  team?: User[]
}
const props = defineProps<Props>()

const emit = defineEmits(['close', 'update:model-value', 'update:order'])

const projectTeam = ref<User[]>(props.team ?? [])
const cancelledBy = ref<number | null>(null)
const cancellationMessage = ref('')
const cancellationReason = ref('')
const cc = ref([])
const distribute = ref(false)
const loading = ref(false)
const otherCancellationReason = ref('')
const step = ref(1)

const cancellationType = computed(() => {
  if (props.order.isInStorePO) {
    return 'STORE'
  }

  if (
    props.order.state.id === OrderState.SIGNING.id
    || props.order.state.id === OrderState.ON_ORDER.id
    || props.order.state.id === OrderState.QUOTES_AWARDED.id
  ) {
    return 'PO'
  }

  return 'ORDER'
})

const localValue = computed({
  get() {
    return props.modelValue
  },
  set(value: boolean) {
    emit('update:model-value', value)
    step.value = 1
  }
})

const buttonLabel = computed(() => isOrder.value ? 'Cancel order' : 'Cancel PO')
const cancellationReasons = computed(() => props.order.cancellationReasons)

const disabled = computed(() => {
  const cancelReason = cancellationReason.value === 'Other' ? otherCancellationReason.value : cancellationReason.value
  return props.selectCanceller ? (!cancelledBy.value || !cancelReason) : !cancelReason
})

const isPO = computed(() => cancellationType.value === 'PO')
const isOrder = computed(() => cancellationType.value === 'ORDER')
const isStore = computed(() => cancellationType.value === 'STORE')

const showDistribute = computed(() => {
  if (isStore.value) {
    return false
  }
  if (props.order.state.id === OrderState.ORDER_SUBMITTED.id && props.order.isFromCallToVendor) {
    return false
  }
  return ![OrderState.SIGNING, OrderState.ON_ORDER, OrderState.COMPLETE, OrderState.ARCHIVED]
    .map(state => state.id)
    .includes(props.order.state.id)
})

const internalDistributionOnly = computed(() => {
  // Transfer is not internal dist because there is no real supplier
  if (props.order.isTransfer) {
    return false
  }
  if (showDistribute.value && !distribute.value) {
    return false
  }
  return isPO.value && props.order.pos[0]?.internal_distribution_only
})

const subtitle = computed(() => {
  let base = `You are going to cancel ${props.order.reference_name}`
  let notify = 'Your supplier and team will be notified of the cancellation with the information below'

  if (isStore.value) {
    base += ` - ${props.order.pos[0].po_number}.`
    notify = 'Your team will be notified of the cancellation with the information below'
  }
  else if (isPO.value && props.order.pos.length && props.order.pos[0].po_number) {
    base += ` - ${props.order.pos[0].po_number}.`
  }
  else {
    base += ` - ${props.order.qtm_reference_number}.`
  }

  if (isStore.value || !showDistribute.value) {
    return `${base} ${notify}`
  }

  return base
})

const title = computed(() => `CANCEL ${isOrder.value ? 'ORDER' : 'PO'}`)

const userEmails = computed(() =>
  projectTeam.value.map(user => ({
    email: user.email,
    name: `${user.first_name} ${user.last_name}`
  }))
)

const { $api, $error, $toast } = useNuxtApp()
onMounted(async () => {
  cancelledBy.value = props.selectCanceller ? props.order.owner?.id : null

  if (projectTeam.value.length) {
    return
  }

  if (props.order.jobsite) {
    try {
      projectTeam.value = await $api.v1.users.list({ jobsite: props.order.jobsite.id })
    }
    catch (error) {
      $error.report(error)
    }
  }

  if (props.order.owner && !projectTeam.value.some(member => member.id === props.order.owner?.id)) {
    projectTeam.value.push(props.order.owner)
  }
})

const cancelOrder = async () => {
  loading.value = true
  try {
    const reason = cancellationReason.value === 'Other' ? otherCancellationReason.value : cancellationReason.value
    const order = await $api.v1.rfqs.cancel(
      props.order.id,
      {
        cancelled_by: props.selectCanceller ? cancelledBy.value : null,
        cancellation_reason: reason,
        cancellation_message: cancellationMessage.value,
        cc: cc.value,
        distribute: distribute.value,
      }
    )
    emit('update:order', order)
    $toast.success(`${isOrder.value ? 'Order' : 'PO'} cancelled successfully`)
    localValue.value = false
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const teamMemberName = (member: User | number) => {
  let user = (typeof member === 'number') ? undefined : member

  if (!user) {
    user = projectTeam.value.find(m => m.id === member)
  }

  return user ? `${user.first_name} ${user.last_name}` : ''
}
</script>

<style scoped>
  .main-text {
    font-family: Roboto Condensed;
    font-style: normal;
    font-weight: bold;
    font-size: 34px;
    line-height: 42px;
    text-align: center;
  }
</style>
